// src/components/Menu.js

import React, { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { UserContext } from '../App';
import Logo from './Logo';
import { fetchAuthSession } from 'aws-amplify/auth';
import { getUserRole, hasAccess, isAdminTenant, clearRoleData, ALLOWED_ROLES } from '../utils/roleUtils';
import { roleTestSuite } from '../utils/roleTestUtils';
import { APP_VERSION } from '../version';

function Menu() {
  const navigate = useNavigate();
  const { userData, handleLogout } = useContext(UserContext);
  const [cognitoDataLoaded, setCognitoDataLoaded] = useState(false);
  const [preferredName, setPreferredName] = useState('');
  const [showVersionTooltip, setShowVersionTooltip] = useState(false);

  // Obtener información de grupos de Cognito desde variables globales (solo para desarrollo)
  const cognitoGroups = process.env.NODE_ENV === 'development' ? window.COGNITO_GROUPS || [] : [];
  const cognitoUser = process.env.NODE_ENV === 'development' ? window.COGNITO_USER || 'No disponible' : '';
  const cognitoTimestamp = process.env.NODE_ENV === 'development' ? window.COGNITO_TIMESTAMP || 'No registrado' : '';

  // Función para convertir texto a formato capital case (primera letra de cada palabra mayúscula)
  const toCapitalCase = (text) => {
    if (!text) return '';
    // Convierte todo a minúsculas y luego capitaliza solo la primera letra de cada palabra
    // Soporta tildes y caracteres especiales correctamente
    return text
      .toLowerCase()
      .replace(/([\p{L}\p{M}'])([\p{L}\p{M}'-]*)/gu, (match, first, rest) =>
        first.toLocaleUpperCase('es-ES') + rest
      )
      .replace(/\b([\p{L}\p{M}'])([\p{L}\p{M}'-]*)/gu, (match, first, rest) =>
        first.toLocaleUpperCase('es-ES') + rest
      );
  };

  // Función para obtener el nombre preferido de Cognito
  const getPreferredNameFromCognito = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        return '';
      }
      
      const payload = idToken.payload || {};
      
      // Opciones de nombre en orden de preferencia:
      // 1. preferred_username (nombre preferido)
      // 2. given_name (nombre de pila)
      // 3. name (nombre completo)
      // 4. email (como último recurso)
      // console.log('🔍 DEBUG - Payload de Cognito:', payload);

      // console.log('🔍 DEBUG - preferred_username:', payload.preferred_username);
      // console.log('🔍 DEBUG - given_name:', payload.given_name);
      // console.log('🔍 DEBUG - name:', payload.name);
      // console.log('🔍 DEBUG - email:', payload.email);

      const preferredName = payload.preferred_username || 
                           payload.given_name || 
                           payload.name || 
                           payload.email?.split('@')[0] || 
                           'Usuario';
      
      return preferredName;
    } catch (error) {
      // console.error('Error al obtener nombre preferido:', error);
      return '';
    }
  };

  // Función para obtener grupos de Cognito (solo para desarrollo)
  const getCognitoGroupsFromMenu = async () => {
    if (process.env.NODE_ENV !== 'development') return [];
    
    try {
      // console.log('🔄 [MENU] Intentando obtener grupos de Cognito...');
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        // console.warn('⚠️ [MENU] No se pudo obtener el ID token');
        return [];
      }
      
      // Obtener el payload del token (donde están los grupos)
      const payload = idToken.payload || {};
      const groups = payload["cognito:groups"] || [];
      
      // Establecer variables globales solo en desarrollo
      window.COGNITO_GROUPS = groups;
      window.COGNITO_USER = payload.email || payload.username || 'No disponible';
      window.COGNITO_TIMESTAMP = new Date().toLocaleTimeString();
      window.COGNITO_FULL_PAYLOAD = payload;
      
      return groups;
    } catch (error) {
      // console.error('❌ [MENU] Error al obtener grupos de Cognito:', error);
      return [];
    }
  };
  
  // Función para verificar acceso en el Menu (doble verificación de seguridad)
  const verifyAccessInMenu = async () => {
    try {
      // console.log('🔒 [MENU] Verificando acceso del usuario...');
      
      const userHasAccess = await hasAccess(ALLOWED_ROLES);
      
      if (!userHasAccess) {
        // console.log('❌ [MENU] Usuario sin acceso válido');
        alert('Tu sesión ha expirado o no tienes permisos para acceder a esta aplicación. Serás redirigido al login.');
        handleLogout();
        return;
      }
      
      // console.log('✅ [MENU] Acceso verificado correctamente');
    } catch (error) {
      // console.error('💥 [MENU] Error al verificar acceso:', error);
      handleLogout();
    }
  };

  // useEffect para obtener datos de Cognito al cargar el Menu
  useEffect(() => {
    const initializeCognitoData = async () => {
      // PRIMERA PRIORIDAD: Verificar acceso de seguridad
      await verifyAccessInMenu();
      
      // Obtener nombre preferido (siempre, tanto en desarrollo como producción)
      const name = await getPreferredNameFromCognito();
      setPreferredName(name);
      
      // Obtener grupos solo en desarrollo
      if (process.env.NODE_ENV === 'development') {
        await getCognitoGroupsFromMenu();
      }
      
      setCognitoDataLoaded(true);
    };
    
    if (!cognitoDataLoaded) {
      initializeCognitoData();
    }
  }, [cognitoDataLoaded]);

  // Estado para el rol del usuario
  const [userRole, setUserRole] = useState(null);
  const [isLoadingRole, setIsLoadingRole] = useState(true);

  // Detectar el rol del usuario usando la nueva lógica
  useEffect(() => {
    const detectRole = async () => {
      try {
        setIsLoadingRole(true);
        // console.log('🔄 [MENU] Iniciando detección de rol...');
        
        // Usar la función utilitaria para obtener el rol
        const role = await getUserRole();
        
        if (role) {
          setUserRole(role);
          // console.log('✅ [MENU] Rol detectado exitosamente:', role);
        } else {
          // console.log('❌ [MENU] No se pudo detectar el rol del usuario');
          // No desloguear inmediatamente, puede ser un problema temporal
        }
      } catch (error) {
        // console.error('💥 [MENU] Error al detectar rol:', error);
      } finally {
        setIsLoadingRole(false);
      }
    };
    
    detectRole();
  }, []); // Sin dependencias para evitar loops

  // Si el usuario no tiene un rol permitido, desloguear
  useEffect(() => {
    if (!isLoadingRole && userRole && !ALLOWED_ROLES.includes(userRole)) {
      // console.log('❌ [MENU] Rol no permitido:', userRole);
      handleLogout();
    }
  }, [userRole, isLoadingRole]);

  // Función para renderizar el menú según el rol
  const renderMenuOptions = () => {
    if (isLoadingRole) {
      return (
        <div className="nav-item">
          <div className="nav-icon">⏳</div>
          <div className="nav-title">Cargando opciones...</div>
        </div>
      );
    }

    if (!userRole) {
      return (
        <div className="nav-item">
          <div className="nav-icon">❌</div>
          <div className="nav-title">Error al cargar menú</div>
        </div>
      );
    }

    // console.log('🎯 [MENU] Renderizando menú para rol:', userRole);
    
    if (userRole === 'admin_tenant') {
      // console.log('👑 [MENU] Renderizando menú completo para admin_tenant');
      return (
        <>
          <div className="nav-item" onClick={() => handleNavigation('/transacciones')}>
            <div className="nav-icon">🔍</div>
            <div className="nav-title">Buscar transacción</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/transacciones-pendientes')}>
            <div className="nav-icon">⏳</div>
            <div className="nav-title">Transacciones pendientes</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/campaigns')}>
            <div className="nav-icon">📢</div>
            <div className="nav-title">Campañas</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/usuarios')}>
            <div className="nav-icon">👥</div>
            <div className="nav-title">Usuarios</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/precios')}>
            <div className="nav-icon">💰</div>
            <div className="nav-title">Precios</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/entidades')}>
            <div className="nav-icon">🏢</div>
            <div className="nav-title">Gestión de Aliados</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/contratos')}>
            <div className="nav-icon">📄</div>
            <div className="nav-title">Plantillas de<br/>Contratos</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/gestion-preguntas')}>
            <div className="nav-icon">❓</div>
            <div className="nav-title">Gestión de<br/>Preguntas</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/gestion-flujos')}>
            <div className="nav-icon">🔄</div>
            <div className="nav-title">Gestión de<br/>Flujos</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/codigos-bonos')}>
            <div className="nav-icon">🎁</div>
            <div className="nav-title">Códigos de<br/>Bonos</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/set-branding')}>
            <div className="nav-icon">🎨</div>
            <div className="nav-title">Personalización<br/>de Marca</div>
          </div>
          <div className="nav-item" onClick={() => handleNavigation('/dashboard-ejecutivo')}>
            <div className="nav-icon">📊</div>
            <div className="nav-title">Dashboard<br/>Ejecutivo</div>
          </div>
        </>
      );
    } else if (ALLOWED_ROLES.includes(userRole)) {
      // console.log('👤 [MENU] Renderizando solo dashboard para rol:', userRole);
      return (
        <div className="nav-item" onClick={() => handleNavigation('/dashboard-ejecutivo')}>
          <div className="nav-icon">📊</div>
          <div className="nav-title">Dashboard<br/>Ejecutivo</div>
        </div>
      );
    } else {
      // console.log('❌ [MENU] Rol no reconocido:', userRole);
      return (
        <div className="nav-item">
          <div className="nav-icon">🚫</div>
          <div className="nav-title">Acceso denegado</div>
        </div>
      );
    }
  };

  // DEBUG: Ver qué datos están llegando al Menu (solo en desarrollo)
  // console.log('🏠 Menu.js - Variables globales de Cognito:');
  // console.log('🏷️ COGNITO_GROUPS:', cognitoGroups);
  // console.log('👤 COGNITO_USER:', cognitoUser);
  // console.log('⏰ COGNITO_TIMESTAMP:', cognitoTimestamp);
  //console.log('🏠 Menu.js - Datos del contexto:');
  //console.log('👤 userData.user_name:', userData.user_name);
  //console.log('🆔 userData.user_id:', userData.user_id);
  //console.log('📦 userData completo:', userData);

  const handleNavigation = (path) => {
    navigate(path);
  };

  return (
    <div className="dashboard-container">
      <header className="header">
        <Logo variant="horizontal" size="medium" />
        <div className="header-title">Panel de Control</div>
        <div className="user-menu">
          <span className="user-name">Hola, {toCapitalCase(userData.user_name || preferredName) || 'Usuario'}</span>
          <button className="btn btn-outline" onClick={handleLogout}>Cerrar Sesión</button>
        </div>
      </header>

      {/* Indicador de versión */}
      <div
        className={`version-badge ${APP_VERSION.environment}`}
        onMouseEnter={() => setShowVersionTooltip(true)}
        onMouseLeave={() => setShowVersionTooltip(false)}
        onClick={() => {
          console.log('📋 Información de la aplicación:', APP_VERSION.getVersionInfo());
        }}
      >
        <span className="version-number">{APP_VERSION.getFullVersion()}</span>
        {process.env.NODE_ENV === 'development' && (
          <span className="version-env">DEV</span>
        )}
      </div>

      {/* Tooltip con información completa */}
      {showVersionTooltip && (
        <div className="version-tooltip">
          <div className="version-tooltip-row">
            <span className="version-tooltip-label">Versión:</span>
            <span className="version-tooltip-value">{APP_VERSION.version}</span>
          </div>
          <div className="version-tooltip-row">
            <span className="version-tooltip-label">Build:</span>
            <span className="version-tooltip-value">{APP_VERSION.buildDate}</span>
          </div>
          <div className="version-tooltip-row">
            <span className="version-tooltip-label">Codename:</span>
            <span className="version-tooltip-value">{APP_VERSION.codename}</span>
          </div>
          <div className="version-tooltip-row">
            <span className="version-tooltip-label">Entorno:</span>
            <span className="version-tooltip-value">{APP_VERSION.environment}</span>
          </div>
        </div>
      )}
      
      <div className="container">
        <div className="card">
          <h2 className="card-title">Panel de Control</h2>
          <p>Bienvenido al sistema de gestión de Retoma. Selecciona una opción para comenzar.</p>
          
                    {/* DEBUG: Información de debugging de roles y acceso (solo en desarrollo) */}
          {process.env.NODE_ENV === 'development' && (
            <div className="debug-info-container">
              <div style={{ color: '#0066cc', fontWeight: 'bold', marginBottom: '10px' }}>
                🔍 DEBUG - Sistema de Roles (DESARROLLO)
              </div>
              <div><strong>👤 Usuario:</strong> {cognitoUser}</div>
              <div><strong>🌟 Nombre Preferido:</strong> {preferredName || 'No disponible'}</div>
              <div><strong>🎭 Rol Actual:</strong> {userRole || '❌ NO DETECTADO'}</div>
              <div><strong>⏳ Cargando Rol:</strong> {isLoadingRole ? 'Sí' : 'No'}</div>
              <div><strong>🏷️ Grupos:</strong> {cognitoGroups.length > 0 ? cognitoGroups.join(', ') : '❌ NINGUNO DETECTADO'}</div>
              <div><strong>📊 Total grupos:</strong> {cognitoGroups.length}</div>
              <div><strong>⏰ Timestamp:</strong> {cognitoTimestamp}</div>
              <div style={{ marginTop: '10px', fontSize: '12px', color: '#666' }}>
                💡 Tip: Abre la consola (F12) para ver todos los logs de roles
              </div>
              <div style={{ marginTop: '10px', display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                <button 
                  onClick={async () => {
                    const name = await getPreferredNameFromCognito();
                    setPreferredName(name);
                    await getCognitoGroupsFromMenu();
                    setCognitoDataLoaded(false); // Forzar re-render
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#0066cc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🔄 Actualizar Datos
                </button>
                <button 
                  onClick={async () => {
                    // console.log('🧪 [DEBUG] Refrescando rol del usuario...');
                    setIsLoadingRole(true);
                    const role = await getUserRole();
                    setUserRole(role);
                    setIsLoadingRole(false);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#059669',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🎭 Refrescar Rol
                </button>
                <button 
                  onClick={() => {
                    // console.log('🧹 [DEBUG] Limpiando datos de rol...');
                    clearRoleData();
                    setUserRole(null);
                    setIsLoadingRole(true);
                    // Recargar después de limpiar
                    setTimeout(async () => {
                      const role = await getUserRole();
                      setUserRole(role);
                      setIsLoadingRole(false);
                    }, 500);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#dc2626',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🧹 Limpiar Cache
                </button>
                <button 
                  onClick={async () => {
                    // console.log('🧪 [DEBUG] Ejecutando test completo del sistema de roles...');
                    const result = await roleTestSuite.runRoleSystemTest();
                    // console.log('🧪 [DEBUG] Resultado del test:', result);
                    alert(`Test completado. Revisa la consola para ver los resultados detallados.\n\nResumen: ${result.success ? 'Éxito' : 'Error'}`);
                  }}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: '#7c3aed',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  🧪 Test Completo
                </button>
              </div>
            </div>
          )}
          
          {/* Información del usuario mejorada */}
          <div className="user-info">
            <div className="user-info-grid">
              <div className="user-info-item">
                <span className="user-info-label">Nombre:</span>
                <span className="user-info-value">{toCapitalCase(userData.user_name) || 'No disponible'}</span>
              </div>
              
              {userData.user_brand && (
                <div className="user-info-item">
                  <span className="user-info-label">Marca:</span>
                  <span className="user-info-value">{toCapitalCase(userData.user_brand)}</span>
                </div>
              )}
              
              {userData.company_name && (
                <div className="user-info-item">
                  <span className="user-info-label">Retailer:</span>
                  <span className="user-info-value">{toCapitalCase(userData.company_name)}</span>
                </div>
              )}
              
              {userData.tenant_id && (
                <div className="user-info-item">
                  <span className="user-info-label">Empresa:</span>
                  <span className="user-info-value">{toCapitalCase(userData.tenant_id)}</span>
                </div>
              )}
            </div>
          </div>
          
          <div className="nav-menu">
            {renderMenuOptions()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Menu;
